// Assistant message — NO bubble background. Plain markdown on the page
// background. Mirrors Reputifly AI Local .msg--ai (App.css:438+).
// While streaming and no content yet, show the inline progress chip
// (.msg__progress) with a spinning dot + rotating word.
// After the reply finishes, show the action row (.msg__actions) — copy
// + regenerate icons, naked, 30×30 hit area.
//
// Files attached by claude via the `reply` tool's `files: []` parameter
// (most common case: screenshots from the snap binary) render INLINE
// when they're images (.png/.jpg/.gif/.webp). Click → Lightbox zoom.
// Non-image files (PDFs, zips, etc.) fall back to a clickable link with
// download icon.

import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { ChevronRight, ExternalLink, Globe } from "react-feather";
import type { MessageMeta, UIMessage } from "../lib/types";
import Markdown from "./Markdown";
import DocumentAttachment from "./DocumentAttachment";
import ImageDownloadControl from "./ImageDownloadControl";
import { assistantFileName } from "../lib/assistantFileName";
import { attachedLink } from "../lib/attachedLink";
import ToolBubble from "./ToolBubble";
import WhatsappHandoffCard from "./WhatsappHandoffCard";
import GeoHandoffCard, { decodeGeoHandoff } from "./GeoHandoffCard";
import AdsResultCard from "./AdsResultCard";
import { useSession } from "../store/session";
import { useAuthedImageUrl } from "../lib/useAuthedImageUrl";
import { authFetch } from "../auth-context";
import { collectGallery } from "../lib/lightbox-gallery";
import { copyTextToClipboard } from "../lib/clipboard";
import { messageTurnIsActive } from "../lib/toolLiveness";
import { currentDelegateActivity, delegatedRequestForMessage, hasDelegateProxyMarker, isCollapsibleDelegateTool, nativeExecutionForMessage, publicUpdateHistory } from "../lib/delegateActivity";
import { validArchive } from "../lib/delegateTimeline";
import { currentClaudeArchive } from "../lib/claudeTimeline";
import { SavedDelegateUpdates } from "./DelegateUpdateList";
import { useArchivedPublicProgress } from "../lib/publicProgressProof";
import { helperCountsFromStatus, helpersReplyNote } from "../lib/helperPresentation";
import { useHelpersSheet } from "../store/helpersSheet";
import { useCostSheet } from "../store/costSheet";
import { extractAdsResult, matchAdImages } from "../lib/adsResult";
import { HelperBot, HelperOrb } from "./HelperFaces";
import SwapText from "./SwapText";
import { FinalLead, ReplyPhaseMarks, UpdatesTail, useReplyPhase } from "./ReplyPhase";

const IMAGE_EXT_RE = /\.(png|jpe?g|gif|webp|heic|heif|avif)$/i;
const PDF_EXT_RE = /\.pdf$/i;
function isImagePath(p: string): boolean { return IMAGE_EXT_RE.test(p); }
function isHttpUrl(p: string): boolean { return /^https?:\/\//i.test(p); }

// Sales-playbook handoff marker. The /sample agent appends `[[handoff:<url>]]`
// as the last line of a sample's delivery message; the frontend strips it from
// the visible text and — ONLY in a sample chat — renders the WhatsApp handoff
// card. Absent the marker, nothing changes (fully additive). Global (no /g flag
// so .replace on a fresh regex is deterministic).
const HANDOFF_RE = /\[\[handoff:\s*(https?:\/\/[^\]\s]+?)\s*\]\]/i;

// GEO audit handoff marker. `/geo-audit` appends `[[geo-handoff:<base64 json>]]`
// carrying only measured values; the sales script itself is hardcoded in
// GeoHandoffCard so the agent cannot garble it. NOT gated on chat kind: an audit
// runs in whichever chat the client lives in. Fully additive — no marker, no change.
const GEO_HANDOFF_RE = /\[\[geo-handoff:\s*([A-Za-z0-9+/=_-]+)\s*\]\]/i;

const PROGRESS_WORDS = [
  "Thinking",
  "Reading",
  "Searching",
  "Synthesizing",
  "Drafting",
  "Cross-checking",
  "Refining",
];

interface Props {
  message: UIMessage;
  isLastInTurn?: boolean;
  publicUpdatesOwned?: boolean;
  publicUpdatesRequestId?: string;
  publicUpdatesActive?: boolean;
  showResponseMeta?: boolean;
}

export default function AssistantBubble({ message, isLastInTurn = true, publicUpdatesOwned = false, publicUpdatesRequestId, publicUpdatesActive = false, showResponseMeta = true }: Props) {
  const isStreaming = !!message.isStreaming;
  const hasContent = (message.content ?? "").length > 0;
  const responseFooter = message.meta ? formatResponseMeta(message.meta) : null;
  const tools = (message.tools ?? []).filter((t) => t.toolName !== "Queue");
  const hasFiles = !!(message.files && message.files.length > 0);

  // Is the OPEN chat a sample? The handoff card renders only for samples, so a
  // real client (or a sample converted to client) never sees "made your site".
  const activeCompanyId = useSession((s) => s.activeCompanyId);
  const status = useSession(s => activeCompanyId ? s.sessionStatusByCompany[activeCompanyId] : undefined);
  const streamingMessageId = useSession(s => s.streamingMessageId);
  const turnIsActive = messageTurnIsActive(message, status, streamingMessageId);
  const messagesByCompany = useSession(s => s.messagesByCompany);
  const queueByCompany = useSession(s => s.queueByCompany);
  const chatMessages = activeCompanyId ? messagesByCompany[activeCompanyId] ?? [] : [];
  const queued = activeCompanyId ? queueByCompany[activeCompanyId] ?? [] : [];
  const currentClaudeUpdates = publicUpdatesOwned && publicUpdatesRequestId
    ? currentClaudeArchive(status, activeCompanyId, publicUpdatesRequestId,
      queued.filter(item => !item.dispatching).flatMap(item => [item.id,item.userMsgId ?? item.id])) : undefined;
  // The request-owned stable frame starts with the exact active placeholder,
  // before native public telemetry arrives. Never flash the old tool wall under
  // that frame. Retain ownership through Pause/final; other requests keep tools.
  const [claudeToolOwner,setClaudeToolOwner] = useState<{companyId:string;requestId:string}|null>(null);
  const hasCurrentClaudeUpdates = publicUpdatesActive || !!currentClaudeUpdates && currentClaudeUpdates.lastSequence > 0;
  if (hasCurrentClaudeUpdates && activeCompanyId && publicUpdatesRequestId &&
      (claudeToolOwner?.companyId !== activeCompanyId || claudeToolOwner.requestId !== publicUpdatesRequestId))
    setClaudeToolOwner({companyId:activeCompanyId,requestId:publicUpdatesRequestId});
  const hasOwnedClaudeUpdates = publicUpdatesOwned && (hasCurrentClaudeUpdates ||
    claudeToolOwner?.companyId === activeCompanyId && claudeToolOwner?.requestId === publicUpdatesRequestId);
  const hasMachineTools = tools.some(tool => isCollapsibleDelegateTool(tool) || hasDelegateProxyMarker(tool));
  const progressScope = turnIsActive
    ? currentDelegateActivity(status, activeCompanyId, chatMessages, queued, streamingMessageId) : null;
  const ownsProgressPanel = progressScope?.messageId === message.id;
  const savedExecution = nativeExecutionForMessage(message);
  const savedArchive = validArchive(message.meta?.delegateArchive);
  const savedRun = savedExecution ?? savedArchive;
  const sameLiveExecution = status?.busy === true && status.delegate?.source === "delegate-runtime" &&
    status.delegate.runId === savedRun?.runId && status.delegate.requestMessageId === savedRun?.requestMessageId &&
    ["waiting", "running", "verifying"].includes(status.delegate.status);
  const hasSavedUpdates = !isStreaming && !sameLiveExecution && (savedArchive || !!savedExecution?.history?.length);
  const delegatedRequest = !ownsProgressPanel && (hasMachineTools || hasSavedUpdates)
    ? delegatedRequestForMessage(chatMessages, queued, message.id, "operations") : undefined;
  const savedScope = savedArchive?.requestMessageId === delegatedRequest ? savedArchive
    : savedExecution?.requestMessageId === delegatedRequest ? savedExecution : undefined;
  const boundScope = ownsProgressPanel ? progressScope?.delegate : savedScope;
  const archivedProgressIds=useArchivedPublicProgress(tools,activeCompanyId,publicUpdatesRequestId);
  const visibleTools = hasOwnedClaudeUpdates ? [] : (ownsProgressPanel || delegatedRequest
    ? tools.filter(tool => !isCollapsibleDelegateTool(tool,boundScope)) : tools)
    .filter(tool=>!archivedProgressIds.has(tool.id));
  const ownsClaudeUpdates = publicUpdatesActive || turnIsActive && !!currentClaudeUpdates;
  const showThinking = turnIsActive && !ownsProgressPanel && !ownsClaudeUpdates;
  const savedUpdates = hasSavedUpdates && savedExecution?.requestMessageId === delegatedRequest
    ? publicUpdateHistory(savedExecution) : [];
  // Helpers (background sub-agents) keep working after this reply; say so
  // under the latest reply so it is not mistaken for the final answer. Same
  // accurate counts as the topbar chip; tapping opens the same Helpers sheet.
  const helperNote = helpersReplyNote(helperCountsFromStatus(status), status?.state === "running" && !!status.autoTurn && status.busy !== true);
  const openHelpers = useHelpersSheet((s) => s.open);
  // Cost Breakdown only where there is something to price: replies from the chat's first
  // priceable moment on (older ones predate the saved receipts). Re-asked when the chat's
  // newest reply changes, so a new chat's first reply gets its button.
  const coverage = useCostSheet((s) => (activeCompanyId ? s.coverage[activeCompanyId] : undefined));
  const replyAt = Date.parse(message.createdAt);
  const costCovered = typeof coverage === "number" && Number.isFinite(replyAt) && replyAt >= coverage - 2_000;
  const isNewestHere = useSession((s) => (activeCompanyId ? s.messagesByCompany?.[activeCompanyId]?.at(-1)?.id === message.id : false));
  // Update (helpers still out) vs Final answer, stamped by the backend (30 Sep 2026).
  const replyPhase = useReplyPhase(message, status, isNewestHere);
  // "Show more" only when an update is actually cut off (measured, not guessed from length).
  const textRef = useRef<HTMLDivElement>(null);
  const [clamped, setClamped] = useState(false);
  useLayoutEffect(() => {
    const el = textRef.current;
    if (!replyPhase.isUpdate || !el) { setClamped(false); return; }
    const measure = () => setClamped(el.scrollHeight > el.clientHeight + 1);
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [replyPhase.isUpdate, message.content]);
  useEffect(() => {
    if (!activeCompanyId || !isLastInTurn) return;
    if (coverage === undefined || (coverage === null && isNewestHere)) useCostSheet.getState().loadCoverage(activeCompanyId);
  }, [activeCompanyId, isLastInTurn, coverage, isNewestHere, message.id]);
  const isLatestReply = !!helperNote &&
    [...chatMessages].reverse().find(m => m.role === "assistant")?.id === message.id;
  const companies = useSession((s) => s.companies);
  const isSampleChat = companies.some(
    (c) => c.id === activeCompanyId && c.kind === "sample",
  );
  // Extract + strip the `[[handoff:<url>]]` marker. displayContent is what we
  // render + what the copy-reply button copies (so the raw marker never shows or
  // gets copied). Gate the CARD on !streaming (full URL present) AND sample chat.
  const handoffMatch = (message.content ?? "").match(HANDOFF_RE);
  const handoffUrl = handoffMatch?.[1] ?? null;
  // Strip BOTH markers before display/copy so neither can leak into the bubble.
  const geoMatch = (message.content ?? "").match(GEO_HANDOFF_RE);
  const geoData = geoMatch ? decodeGeoHandoff(geoMatch[1]) : null;
  let displayContent = message.content ?? "";
  if (handoffUrl) displayContent = displayContent.replace(HANDOFF_RE, "");
  if (geoMatch) displayContent = displayContent.replace(GEO_HANDOFF_RE, "");
  displayContent = displayContent.replace(/\s+$/, "");
  const showHandoff = !!handoffUrl && isSampleChat && !isStreaming;
  const showGeoHandoff = !!geoData && !isStreaming;
  // An ads run's ```ads-result block renders as a card in its place. Invalid or
  // absent: null, and the reply renders exactly as it always has.
  const adsBlock = useMemo(() => extractAdsResult(displayContent), [displayContent]);
  const adImages = useMemo(() => adsBlock ? matchAdImages(adsBlock.result.ads, message.files ?? []) : null,
    [adsBlock, message.files]);
  // Images the card shows are not listed a second time below it.
  const listedFiles = (message.files ?? []).filter(file => !adImages?.used.has(file));
  // Copy takes the words around the card, not its raw data block.
  const copyContent = adsBlock ? [adsBlock.before, adsBlock.after].filter(part => part.trim()).join("\n\n") || displayContent
    : displayContent;
  // Hooks BEFORE any early returns (rules of hooks).
  const [progressIdx, setProgressIdx] = useState(0);
  const [progressFading, setProgressFading] = useState(false);
  const [copied, setCopied] = useState(false);
  const copyTimer = useRef<number | null>(null);
  // Elapsed-time tracker for the progress chip — see ToolBubble for the
  // same rationale. Captures the moment the placeholder first becomes
  // streaming-with-no-content and shows "(12s)" after 10s, "(1m 23s)"
  // after 60s. Stops ticking the moment content arrives.
  const progressStartedRef = useRef<number | null>(null);
  const [progressNow, setProgressNow] = useState<number>(() => Date.now());
  useEffect(() => {
    if (!showThinking || hasContent) {
      progressStartedRef.current = null;
      return;
    }
    if (progressStartedRef.current === null) progressStartedRef.current = Date.now();
    const id = window.setInterval(() => setProgressNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [showThinking, hasContent]);
  const progressElapsedMs =
    progressStartedRef.current != null
      ? Math.max(0, progressNow - progressStartedRef.current)
      : 0;
  const showProgressElapsed = showThinking && !hasContent && progressElapsedMs >= 10_000;
  const formattedProgressElapsed = (() => {
    const s = Math.floor(progressElapsedMs / 1000);
    if (s < 60) return `${s}s`;
    const m = Math.floor(s / 60);
    const rem = s % 60;
    return rem === 0 ? `${m}m` : `${m}m ${rem}s`;
  })();

  // Rotating word during streaming, cross-fade every ~2.4s.
  useEffect(() => {
    if (!showThinking || hasContent) return;
    const id = window.setInterval(() => {
      setProgressFading(true);
      window.setTimeout(() => {
        setProgressIdx((i) => (i + 1) % PROGRESS_WORDS.length);
        setProgressFading(false);
      }, 220);
    }, 2400);
    return () => window.clearInterval(id);
  }, [showThinking, hasContent]);

  useEffect(() => {
    return () => {
      if (copyTimer.current) window.clearTimeout(copyTimer.current);
    };
  }, []);

  async function copyToClipboard() {
    if (!hasContent) return;
    // Copy the raw reply text rather than rendered DOM. This preserves every
    // fenced curl/code block exactly as Claude sent it, while excluding only
    // the private handoff marker already removed from displayContent. An ads
    // card's raw data block is left out: the words around it are copied.
    const didCopy = await copyTextToClipboard(copyContent);
    if (!didCopy) {
      useSession.getState().setToast("Couldn't copy — clipboard blocked", "error");
      return;
    }
    setCopied(true);
    if (copyTimer.current) window.clearTimeout(copyTimer.current);
    copyTimer.current = window.setTimeout(() => setCopied(false), 1500);
  }

  // Empty husk — no text, no tools, no files, not actively streaming.
  // Previously rendered "(no content)" which is a confusing artifact when an
  // SSE event drops or a placeholder gets orphaned. Just hide the whole
  // bubble; the sidebar's busy-dot signals actual ongoing work.
  if (!hasContent && !isStreaming && tools.length === 0 && !hasFiles) {
    return null;
  }
  // An earlier update of this request, folded under its newest reply (30 Sep 2026).
  if (replyPhase.hidden) return null;

  return (
    <div data-mid={message.id} className={"msg msg--ai" + (replyPhase.isUpdate ? " msg--update" : "") + (replyPhase.isUpdate && replyPhase.open ? " is-open" : "")}>
      {visibleTools.length > 0 && (
        <div className="msg__tools">
          {visibleTools.map((t) => (
            <ToolBubble
              key={t.id}
              toolName={t.toolName}
              args={t.args}
              status={t.status}
              active={turnIsActive}
              result={t.result}
            />
          ))}
        </div>
      )}

      {!publicUpdatesOwned && hasSavedUpdates && savedScope && activeCompanyId && <SavedDelegateUpdates entries={savedUpdates}
        companyId={activeCompanyId} runId={savedScope.runId} requestMessageId={savedScope.requestMessageId}
        archive={savedArchive?.requestMessageId === delegatedRequest ? savedArchive : undefined} />}
      {!hasContent && showThinking ? (
        <div className={`msg__progress ${progressFading ? "is-fading" : ""}`}>
          {PROGRESS_WORDS[progressIdx]}…
          {showProgressElapsed && (
            <span className="msg__progressElapsed"> · {formattedProgressElapsed}</span>
          )}
        </div>
      ) : hasContent && adsBlock && adImages ? (
        <>
          {adsBlock.before && <div className="msg__text"><Markdown text={adsBlock.before} /></div>}
          <AdsResultCard result={adsBlock.result} images={adImages.images} />
          {adsBlock.after.trim() && <div className="msg__text"><Markdown text={adsBlock.after} /></div>}
        </>
      ) : hasContent ? (
        <div className="msg__text" ref={textRef}>
          <Markdown text={displayContent} />
        </div>
      ) : null}

      {showHandoff && handoffUrl && <WhatsappHandoffCard url={handoffUrl} />}
      {showGeoHandoff && geoData && (
        <GeoHandoffCard
          data={geoData}
          renderFile={(path) => <AssistantFile fileRef={path} responseText={displayContent} />}
        />
      )}

      {listedFiles.length > 0 && (
        // data-lb-group: clicking any image opens this message's whole image
        // set as a ←/→ navigable gallery (see lib/lightbox-gallery.ts).
        <div className="msg__aiFiles" data-lb-group>
          {listedFiles.map((f, i) => (
            <AssistantFile key={`${f}-${i}`} fileRef={f} responseText={displayContent} />
          ))}
        </div>
      )}

      {hasContent && !isStreaming && (isLastInTurn || (isLatestReply && !!helperNote)) && (
        <div className="msg__actions">
          {isLastInTurn && (
          <button
            type="button"
            className={"msg__actionBtn msg__actionBtn--copy" + (copied ? " is-copied" : "")}
            onClick={copyToClipboard}
            aria-label={copied ? "Reply copied" : "Copy reply"}
            title={copied ? "Copied" : "Copy"}
          >
            {copied ? (
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" width="15" height="15">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" width="15" height="15">
                <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
              </svg>
            )}
          </button>
          )}
          {/* Cost breakdown (28 Sep): a receipt for the turn this reply ended — tokens and cost
              per agent. Same round button as Copy; opens the receipt sheet. */}
          {isLastInTurn && activeCompanyId && costCovered && (
          <button
            type="button"
            className="msg__actionBtn msg__actionBtn--receipt"
            onClick={() => useCostSheet.getState().open(activeCompanyId, Date.parse(message.createdAt))}
            aria-haspopup="dialog"
            aria-label="Cost breakdown for this reply"
            title="Cost breakdown"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" width="15" height="15" aria-hidden="true">
              <path d="M5 3h14v18l-2.33-1.5L14.33 21 12 19.5 9.67 21l-2.34-1.5L5 21z" />
              <path d="M9 8h6M9 12h6M9 16h3" />
            </svg>
          </button>
          )}
          {/* Helpers still at work after this reply: one more pill in the row, so the reply is not
              read as the final answer. Same counts as the topbar chip; opens the Helpers sheet. */}
          {isLatestReply && helperNote && activeCompanyId && (
            <button type="button" className={"msg__helpersBtn is-" + helperNote.tone} aria-haspopup="dialog"
              aria-label={`${helperNote.text} Show helpers`} title={helperNote.text}
              onClick={(event) => openHelpers(activeCompanyId, event.currentTarget)}>
              {helperNote.tone === "working" ? <HelperOrb /> : <span className="msg__helpersDot" aria-hidden="true" />}
              <SwapText className="msg__helpersText" text={helperNote.short} shimmer={helperNote.tone === "working"} />
              {helperNote.faces.length > 0 && (
                <span className="msg__helpersFaces" aria-hidden="true">
                  {helperNote.faces.map((bot, i) => <HelperBot key={`${bot}-${i}`} bot={bot} state="running" size={20} />)}
                </span>
              )}
              <ChevronRight width={14} height={14} aria-hidden="true" />
            </button>
          )}
        </div>
      )}

      {hasContent && !isStreaming && <ReplyPhaseMarks phase={replyPhase} text={displayContent} clamped={clamped} />}
      {/* Response footer: duration · thinking level · type · model. Purely informational —
          rendered only when the backend stamped meta on this reply; absent ⇒ nothing shows.
          Independent of isLastInTurn so it appears under EVERY assistant response. */}
      {/* An update keeps only its "N updates" link here (no timing line of its own). */}
      {(() => {
        const footerText = showResponseMeta && responseFooter && !replyPhase.isUpdate ? responseFooter.text : null;
        // Date/time (Singapore) after a separator, on replies that carry a footer.
        const timeText = footerText ? formatMessageTime(message.createdAt) : "";
        const metaText = [footerText, timeText].filter(Boolean).join(" · ") || null;
        if (!hasContent || isStreaming || !(metaText || replyPhase.isFinal || replyPhase.foldedUpdates > 0)) return null;
        return (
          <div className="msg__meta" title={metaText ? responseFooter?.title : undefined}
            aria-label={[replyPhase.isFinal ? "Final answer" : "", metaText ? `${metaText}. ${responseFooter?.title ?? ""}` : ""].filter(Boolean).join(". ") || undefined}>
            <FinalLead phase={replyPhase} more={!!metaText} />
            {metaText}
            <UpdatesTail phase={replyPhase} lead={replyPhase.isFinal || !!metaText} />
          </div>
        );
      })()}
    </div>
  );
}

/** Attribution belongs to this saved message; never consult the active run. */
export function formatResponseMeta(meta: MessageMeta): { text: string; title: string } {
  const delegate = meta.delegate;
  const id = /^[a-zA-Z0-9][a-zA-Z0-9_-]{0,127}$/;
  const execution = meta.delegateExecution;
  // Saved metadata is authoritative: never label a historical model Opus 5 or
  // borrow the current picker's effort for an older response.
  const relayModel = [meta.model, meta.model ? meta.effort : ""].filter(Boolean).join(" ");
  if (execution?.source === "native-delegate-execution" && execution.model === "gpt-6-astra" &&
      typeof execution.runId === "string" && id.test(execution.runId) &&
      typeof execution.requestMessageId === "string" && id.test(execution.requestMessageId) &&
      ["high", "xhigh", "max"].includes(execution.rootEffort) &&
      ["completed", "failed", "cancelled"].includes(execution.status) &&
      (!delegate || (delegate.runId === execution.runId && delegate.requestMessageId === execution.requestMessageId &&
        delegate.model === execution.model && delegate.rootEffort === execution.rootEffort))) {
    const measured = (value?: number) => typeof value === "number" && Number.isFinite(value) && value >= 0
      ? value === 0 ? "0s" : fmtDuration(value) : "";
    const attempt = Number.isInteger(execution.attempt) && Number(execution.attempt) > 0 ? Number(execution.attempt) : null;
    const nativeTime = measured(execution.executionElapsedMs), relayTime = !attempt || attempt === 1 ? measured(execution.relayOverheadMs) : "";
    const total = measured(meta.durationMs);
    const outcome = execution.status === "failed" ? "Not completed" : execution.status === "cancelled" ? "Stopped"
      : delegate?.source === "verified-delegate-final" ? "" : "Execution ended";
    return {
      text: [
        [`Astra ${{ high: "High", xhigh: "xHigh", max: "Max" }[execution.rootEffort]}`, nativeTime].filter(Boolean).join(" · "),
        relayModel ? [relayModel, relayTime].filter(Boolean).join(" · ") : "",
      ].filter(Boolean).join(" · "),
      title: [outcome ? `Run status: ${outcome}.` : "", meta.type ? `Request: ${meta.type}.` : "",
        attempt && attempt > 1 ? `Attempt ${attempt}.` : "", total ? `Total: ${total}.` : "",
        "Saved native model execution receipt, not a task-success claim. Astra execution is the latest attempt's wall time including tools, not an all-attempt total. The relay model's time includes relay / wait, not active Opus thinking."].filter(Boolean).join(" "),
    };
  }
  if (delegate?.source === "verified-delegate-final" && delegate.model === "gpt-6-astra" &&
      typeof delegate.runId === "string" && typeof delegate.requestMessageId === "string" &&
      id.test(delegate.runId) && id.test(delegate.requestMessageId) &&
      ["high", "xhigh", "max"].includes(delegate.rootEffort)) {
    const effort = { high: "High", xhigh: "xHigh", max: "Max" }[delegate.rootEffort];
    const duration = Number.isFinite(meta.durationMs) ? fmtDuration(meta.durationMs) : "";
    return {
      text: [`Astra ${effort}`, relayModel, duration ? `Total · ${duration}` : ""].filter(Boolean).join(" · "),
      title: [meta.type ? `Request: ${meta.type}.` : "",
        "Astra model and effort verified for this saved answer. Separate model durations are unavailable. Total is whole-turn elapsed time, including waiting and tools, not a model-time split or quality score."].filter(Boolean).join(" "),
    };
  }
  // An older saved "Ultracode" / "Ultra" segment (the tier retired 9 Oct 2026) reads as Max.
  // Inline on purpose (same rule as lib/effortLevel footerEffortLabel): unit tests load this file alone.
  const effort = meta.effort && /^\s*ultra(code)?\s*$/i.test(meta.effort) ? "Max" : meta.effort;
  return { text: [fmtDuration(meta.durationMs), effort, meta.type, meta.model].filter(Boolean).join(" · "),
    title: "How this response ran" };
}

/** ms → "42s" / "12m 04s" / "1h 03m". Empty string if missing. */
function fmtDuration(ms?: number): string {
  if (!ms || ms < 0) return "";
  const total = Math.round(ms / 1000);
  if (total < 60) return `${total}s`;
  const m = Math.floor(total / 60);
  const s = total % 60;
  if (m < 60) return `${m}m ${String(s).padStart(2, "0")}s`;
  const h = Math.floor(m / 60);
  return `${h}h ${String(m % 60).padStart(2, "0")}m`;
}

/* =========================================================================
   AssistantFile — render one file from message.files[]
   ========================================================================= */
/**
 * One of three render paths based on the file reference:
 *   1. http(s) URL  → external link (opens in new tab; works for PDFs hosted
 *      on client's WordPress media library — claude's "host PDF on WP" pattern).
 *   2. Image local path (inbox/<cid>/snap-*.png) → fetch via useAuthedImageUrl
 *      → render as inline <img> with click-to-Lightbox-zoom. Mirrors the
 *      pattern UserBubble's Attachment component uses.
 *   3. Other local path (PDFs, zips, files) → link icon + filename; the click
 *      fetches the bytes via the authed /api/uploads/:subdir/:filename endpoint
 *      and opens (PDF) or downloads (everything else).
 *
 * Path extraction for the image case mirrors useAuthedImageUrl: takes the
 * last 2 path segments (subdir + filename) and fetches via the authed
 * /api/uploads/<subdir>/<filename> endpoint. snap saves to
 * /home/claude/agent/inbox/<companyId>/snap-<ts>-<viewport>.png so segments
 * are <companyId>/<filename> — same depth as user uploads.
 */
function AssistantFile({ fileRef, responseText }: { fileRef: string; responseText: string }) {
  const openLightbox = useSession((s) => s.openLightbox);
  const setToast = useSession((s) => s.setToast);
  const isUrl = isHttpUrl(fileRef);
  const isImg = !isUrl && isImagePath(fileRef);
  const filename = assistantFileName(fileRef);
  const [opening, setOpening] = useState(false);
  const openingRef = useRef(false);

  // Hook called UNCONDITIONALLY (rules of hooks) — undefined arg short-circuits.
  // For non-image paths, we never use the fetched URL.
  const img = useAuthedImageUrl(isImg ? fileRef : undefined);

  // Path 1: http URL
  if (isUrl) {
    let link;
    try { link = attachedLink(fileRef, responseText); }
    catch { return <span className="msg__fileUnavailable">Shared link unavailable</span>; }
    if (link.filename) return <DocumentAttachment attachment={{ name: link.filename, mime: PDF_EXT_RE.test(link.filename) ? 'application/pdf' : 'application/octet-stream' }}
      href={fileRef} external domain={link.domain} />;
    return (
      <a
        href={fileRef}
        target="_blank"
        rel="noreferrer noopener"
        className="msg__file msg__file--external"
        referrerPolicy="no-referrer"
        aria-label={`${link.title}, ${link.domain}, opens in a new tab`}
      >
        <span className="msg__fileLinkIcon" aria-hidden="true"><Globe width={22} height={22} data-icon="globe" /></span>
        <span className="msg__fileLinkBody"><strong>{link.title}</strong><span>{link.domain}</span></span>
        <ExternalLink className="msg__fileLinkArrow" width={18} height={18} aria-hidden="true" />
      </a>
    );
  }

  // Path 2: image local path → inline render + click-to-zoom
  if (isImg && img.status === "loaded" && img.url) {
    return (
      <div className="msg__aiImage" role="group" aria-label={`Image ${filename}`}>
      <button
        type="button"
        className="msg__aiImageOpen"
        onClick={(e) => {
          const g = collectGallery(e.currentTarget, { blobUrl: img.url!, filename });
          openLightbox(g.items, g.index);
        }}
        title={`${filename} — tap to view full size`}
        aria-label={`Open ${filename}`}
      >
        <img
          src={img.url}
          alt={filename}
          draggable={false}
          loading="lazy"
          decoding="async"
          data-lb-src={img.url}
          data-lb-name={filename}
        />
      </button>
      <ImageDownloadControl src={img.url} filename={filename} />
      </div>
    );
  }

  // Path 2 (error): fetch failed (file missing / relocated away / never written
  // / token expired) → a graceful "unavailable" placeholder instead of an
  // ETERNAL SPINNER (the old behavior). The agent may have attached a path to a
  // file that no longer exists; the operator now sees that, not a frozen load.
  if (isImg && img.status === "error") {
    return (
      <div
        className="msg__aiImage msg__aiImage--error"
        title={`${filename} — image unavailable (the file may no longer exist on the server)`}
        aria-label={`${filename} unavailable`}
      >
        <span>⚠️ image unavailable</span>
      </div>
    );
  }

  // Path 2 (loading): image but fetch in flight → placeholder
  if (isImg) {
    return (
      <div className="msg__aiImage msg__aiImage--loading" aria-label="Loading image">
        <span className="spinner" />
      </div>
    );
  }

  // Path 3: every non-image local file → FETCH ON CLICK and download.
  //
  // This used to be `href="#"` + preventDefault — a dead link. It showed the
  // filename with a PDF icon and did nothing when tapped, so an agent-generated
  // audit PDF was effectively unreachable from the chat. The bytes were always
  // there; only the click was missing.
  //
  // Deliberately fetched ON CLICK, not on mount (unlike images): a chat can carry
  // several multi-MB PDFs and eagerly downloading them all would be wasteful. The
  // file is served by the same authed GET /api/uploads/:subdir/:filename the image
  // hook uses, so nothing new is exposed. Never open active document content.
  const openLocalFile = async () => {
    if (openingRef.current) return;
    const parts = fileRef.split("/").filter(Boolean);
    if (parts.length < 2) {
      setToast("Can't download: the file path looks incomplete.", "error");
      return;
    }
    const fname = parts[parts.length - 1]!;
    const subdir = parts[parts.length - 2]!;
    openingRef.current = true;
    setOpening(true);
    try {
      const r = await authFetch(
        `/api/uploads/${encodeURIComponent(subdir)}/${encodeURIComponent(fname)}`,
      );
      if (!r.ok) {
        setToast(
          r.status === 404
            ? "That file is no longer on the server."
            : `Couldn't download the file (${r.status}).`,
          "error",
        );
        return;
      }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      // Give the tab/download time to take the blob before releasing it.
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch {
      setToast("Couldn't download the file. Check your connection and try again.", "error");
    } finally {
      openingRef.current = false;
      setOpening(false);
    }
  };
  return <DocumentAttachment attachment={{ mime: PDF_EXT_RE.test(fileRef) ? 'application/pdf' : 'application/octet-stream', name: filename }}
    onDownload={() => { void openLocalFile(); }} downloading={opening} />;
}

// Message date/time in Singapore time, e.g. "April 20, 4:20 AM" (year added when not this year).
const SGT = "Asia/Singapore";
function formatMessageTime(iso: string | undefined | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const year = (x: Date) => new Intl.DateTimeFormat("en-US", { timeZone: SGT, year: "numeric" }).format(x);
  const opts: Intl.DateTimeFormatOptions = { timeZone: SGT, month: "long", day: "numeric", hour: "numeric", minute: "2-digit", hour12: true };
  if (year(d) !== year(new Date())) opts.year = "numeric";
  // "October 9, 7:06 PM" (Intl would say "October 9 at 7:06 PM").
  const parts = new Intl.DateTimeFormat("en-US", opts).formatToParts(d);
  const get = (t: string) => parts.find((x) => x.type === t)?.value ?? "";
  const date = `${get("month")} ${get("day")}${opts.year ? `, ${get("year")}` : ""}`;
  return `${date}, ${get("hour")}:${get("minute")} ${get("dayPeriod")}`;
}
